import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import CreateUnitPage from './pages/CreateUnitPage';
import UnitsListPage from './pages/UnitsListPage';
import SetupWizardPage from './pages/SetupWizardPage';
import UnitRouteLayout from '@/components/UnitRouteLayout';
import TreasurerShell from '@/components/TreasurerShell';
import TreasurerDashboardPage from './pages/TreasurerDashboardPage';
import MembersListPage from './pages/MembersListPage';
import MemberFormPage from './pages/MemberFormPage';
import SettingsPage from './pages/SettingsPage';
import PlaceholderPage from './pages/PlaceholderPage';
import AuthHandoffPage from './pages/AuthHandoffPage';

/**
 * Routes that require the user to be signed in. Redirects to /login when
 * the auth state has finished loading and there is no user.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { currentUser, loading } = useAuth();
  if (loading) return null;
  if (!currentUser) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          {/* Admin handoff entry point — exchanges a custom token for a
              session and forwards into the treasurer area. Public route
              because the user isn't yet signed in when they land here. */}
          <Route path="/auth/handoff" element={<AuthHandoffPage />} />
          <Route path="/unit/create" element={<RequireAuth><CreateUnitPage /></RequireAuth>} />

          {/* Pick a unit. Default landing for an authenticated user. */}
          <Route path="/units" element={<RequireAuth><UnitsListPage /></RequireAuth>} />
          <Route path="/dashboard" element={<Navigate to="/units" replace />} />

          {/* Per-unit treasurer area — UnitProvider wraps all child routes. */}
          <Route path="/u/:unitId" element={<RequireAuth><UnitRouteLayout /></RequireAuth>}>
            {/* Setup wizard lives outside the shell but still needs unit context. */}
            <Route path="setup" element={<SetupWizardPage />} />

            <Route element={<TreasurerShell />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<TreasurerDashboardPage />} />
              <Route
                path="banking"
                element={
                  <PlaceholderPage
                    title="Open Banking"
                    description="Connect bank accounts and import transactions for reconciliation."
                    stage="Stage 2 (Open Banking)"
                  />
                }
              />
              <Route path="members" element={<MembersListPage />} />
              <Route path="members/new" element={<MemberFormPage />} />
              <Route path="members/:memberId" element={<MemberFormPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route
                path="transactions"
                element={
                  <PlaceholderPage
                    title="Transactions"
                    description="Imported bank transactions and manual journal entries appear here."
                    stage="Stage 2 (Open Banking)"
                  />
                }
              />
              <Route
                path="subscriptions"
                element={
                  <PlaceholderPage
                    title="Subscriptions"
                    description="Track who has paid their annual lodge dues."
                    stage="Stage 4 (Subscriptions)"
                  />
                }
              />
              <Route
                path="reports"
                element={
                  <PlaceholderPage
                    title="Reports"
                    description="Lodge meeting report, annual statement, audit trail, charity fund report and Grand Lodge return."
                    stage="Stage 5 (Reports)"
                  />
                }
              />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
